import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  ClinicalNote,
  ClinicalNotePayload,
  ClinicalNoteType,
  SessionSchedule,
  createClinicalNote,
  getApiErrorMessage,
  getCollaboratorSessions,
  updateClinicalNote,
} from "src/apis/prontuario";
import { Id } from "src/utils/types";
import {
  formatSessionDate,
  formatSessionLabel,
  getClinicalNoteId,
  getNoteSelectionId,
  getSessionSelectionId,
  noteTypeLabels,
  parseSessionSelectionId,
} from "./utils";

type ClinicalNoteFormProps = {
  prontuarioId: Id;
  idColaborador: Id;
  note?: ClinicalNote | null;
  onCancel: () => void;
  onSaved: () => void;
  onNotify: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
};

type ClinicalNoteFormValues = {
  session_selection: string;
  tipo_anotacao: ClinicalNoteType;
  titulo: string;
  queixa_relato: string;
  observacao_clinica: string;
  intervencoes_realizadas: string;
  resposta_do_paciente: string;
  evolucao_percebida: string;
  hipoteses_clinicas: string;
  fatores_de_risco: string;
  fatores_de_protecao: string;
  plano_conduta: string;
  encaminhamentos: string;
  soap_subjetivo: string;
  soap_objetivo: string;
  soap_avaliacao: string;
  soap_plano: string;
  humor_nota: number;
  ansiedade_nota: number;
  adesao_nota: number;
  risco_nota: number;
  evolucao_nota: number;
  tags: string;
};

const defaultValues: ClinicalNoteFormValues = {
  session_selection: "",
  tipo_anotacao: "evolucao",
  titulo: "",
  queixa_relato: "",
  observacao_clinica: "",
  intervencoes_realizadas: "",
  resposta_do_paciente: "",
  evolucao_percebida: "",
  hipoteses_clinicas: "",
  fatores_de_risco: "",
  fatores_de_protecao: "",
  plano_conduta: "",
  encaminhamentos: "",
  soap_subjetivo: "",
  soap_objetivo: "",
  soap_avaliacao: "",
  soap_plano: "",
  humor_nota: 3,
  ansiedade_nota: 3,
  adesao_nota: 4,
  risco_nota: 1,
  evolucao_nota: 4,
  tags: "",
};

const labels: Record<keyof ClinicalNoteFormValues, string> = {
  session_selection: "Sessão ou atendimento",
  tipo_anotacao: "Tipo",
  titulo: "Título",
  queixa_relato: "Queixa/relato",
  observacao_clinica: "Observação clínica",
  intervencoes_realizadas: "Intervenções realizadas",
  resposta_do_paciente: "Resposta do paciente",
  evolucao_percebida: "Evolução percebida",
  hipoteses_clinicas: "Hipóteses clínicas",
  fatores_de_risco: "Fatores de risco",
  fatores_de_protecao: "Fatores de proteção",
  plano_conduta: "Plano/conduta",
  encaminhamentos: "Encaminhamentos",
  soap_subjetivo: "SOAP subjetivo",
  soap_objetivo: "SOAP objetivo",
  soap_avaliacao: "SOAP avaliação",
  soap_plano: "SOAP plano",
  humor_nota: "Humor",
  ansiedade_nota: "Ansiedade",
  adesao_nota: "Adesão",
  risco_nota: "Risco",
  evolucao_nota: "Evolução",
  tags: "Tags",
};

const narrativeFields: Array<keyof ClinicalNoteFormValues> = [
  "queixa_relato",
  "observacao_clinica",
  "intervencoes_realizadas",
  "resposta_do_paciente",
  "evolucao_percebida",
  "hipoteses_clinicas",
  "fatores_de_risco",
  "fatores_de_protecao",
  "plano_conduta",
  "encaminhamentos",
];

const soapFields: Array<keyof ClinicalNoteFormValues> = [
  "soap_subjetivo",
  "soap_objetivo",
  "soap_avaliacao",
  "soap_plano",
];

const scoreFields: Array<keyof ClinicalNoteFormValues> = [
  "humor_nota",
  "ansiedade_nota",
  "adesao_nota",
  "risco_nota",
  "evolucao_nota",
];

const normalizeScore = (value: number): number => {
  if (Number.isNaN(value)) {
    return 1;
  }

  return Math.min(5, Math.max(1, value));
};

const parseTags = (tags: string): string[] => {
  return tags
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const normalizeFormValues = (note?: ClinicalNote | null): ClinicalNoteFormValues => {
  if (!note) {
    return defaultValues;
  }

  return {
    session_selection: getNoteSelectionId(note),
    tipo_anotacao: (note.tipo_anotacao as ClinicalNoteType) ?? "evolucao",
    titulo: String(note.titulo ?? ""),
    queixa_relato: String(note.queixa_relato ?? ""),
    observacao_clinica: String(note.observacao_clinica ?? ""),
    intervencoes_realizadas: String(note.intervencoes_realizadas ?? ""),
    resposta_do_paciente: String(note.resposta_do_paciente ?? ""),
    evolucao_percebida: String(note.evolucao_percebida ?? ""),
    hipoteses_clinicas: String(note.hipoteses_clinicas ?? ""),
    fatores_de_risco: String(note.fatores_de_risco ?? ""),
    fatores_de_protecao: String(note.fatores_de_protecao ?? ""),
    plano_conduta: String(note.plano_conduta ?? ""),
    encaminhamentos: String(note.encaminhamentos ?? ""),
    soap_subjetivo: String(note.soap_subjetivo ?? ""),
    soap_objetivo: String(note.soap_objetivo ?? ""),
    soap_avaliacao: String(note.soap_avaliacao ?? ""),
    soap_plano: String(note.soap_plano ?? ""),
    humor_nota: Number(note.humor_nota ?? 3),
    ansiedade_nota: Number(note.ansiedade_nota ?? 3),
    adesao_nota: Number(note.adesao_nota ?? 4),
    risco_nota: Number(note.risco_nota ?? 1),
    evolucao_nota: Number(note.evolucao_nota ?? 4),
    tags: (note.tags ?? []).join(", "),
  };
};

const buildPayload = (
  currentValues: ClinicalNoteFormValues,
  originalValues: ClinicalNoteFormValues,
  includeAllFields: boolean
): ClinicalNotePayload => {
  return (Object.keys(currentValues) as Array<keyof ClinicalNoteFormValues>).reduce<
    ClinicalNotePayload
  >((payload, key) => {
    if (!includeAllFields && currentValues[key] === originalValues[key]) {
      return payload;
    }

    if (key === "session_selection") {
      const { fk_service_id, fk_session_schedule_id } = parseSessionSelectionId(
        currentValues.session_selection
      );
      payload.fk_service_id = fk_service_id;
      payload.fk_session_schedule_id = fk_session_schedule_id;
      return payload;
    }

    if (key === "tags") {
      payload.tags = parseTags(currentValues.tags);
      return payload;
    }

    if (scoreFields.includes(key)) {
      payload[key] = normalizeScore(Number(currentValues[key])) as never;
      return payload;
    }

    payload[key] = currentValues[key] as never;
    return payload;
  }, {});
};

const ClinicalNoteForm: FC<ClinicalNoteFormProps> = ({
  prontuarioId,
  idColaborador,
  note,
  onCancel,
  onSaved,
  onNotify,
}) => {
  const isEdit = Boolean(note);
  const originalValues = useMemo(() => normalizeFormValues(note), [note]);
  const [formValues, setFormValues] = useState<ClinicalNoteFormValues>(originalValues);
  const [loading, setLoading] = useState<boolean>(false);
  const [sessions, setSessions] = useState<SessionSchedule[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  useEffect(() => {
    setFormValues(originalValues);
  }, [originalValues]);

  const fetchSessions = useCallback(async () => {
    if (!idColaborador) {
      setSessionsError("ID do colaborador não informado.");
      return;
    }
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const data = await getCollaboratorSessions(idColaborador);
      setSessions(data);
    } catch (err: unknown) {
      setSessionsError(
        getApiErrorMessage(err, "Não foi possível carregar as sessões.")
      );
    } finally {
      setSessionsLoading(false);
    }
  }, [idColaborador]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // O vínculo já salvo pode não estar na lista (agendamento cancelado, atendimento
  // excluído), e sem uma opção correspondente o Select apareceria vazio.
  const sessionOptions = useMemo(() => {
    const options: Array<{ id: string; label: string }> = [];
    const seenIds = new Set<string>();

    sessions.forEach((session) => {
      const selectionId = getSessionSelectionId(session);

      if (!selectionId || seenIds.has(selectionId)) {
        return;
      }

      seenIds.add(selectionId);
      options.push({ id: selectionId, label: formatSessionLabel(session) });
    });

    const currentSelection = originalValues.session_selection;

    if (currentSelection && !seenIds.has(currentSelection)) {
      options.unshift({
        id: currentSelection,
        label: `Vínculo atual · ${formatSessionDate(note?.data_atendimento)}`,
      });
    }

    return options;
  }, [sessions, originalValues.session_selection, note]);

  const handleChange = (
    field: keyof ClinicalNoteFormValues,
    value: string | number | ClinicalNoteType | null
  ) => {
    setFormValues((previousValues) => ({
      ...previousValues,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload = buildPayload(formValues, originalValues, !isEdit);

    if (isEdit && Object.keys(payload).length === 0) {
      onNotify("Nenhuma alteração para salvar.", "info");
      return;
    }

    setLoading(true);

    try {
      if (isEdit && note) {
        const noteId = getClinicalNoteId(note);

        if (!noteId) {
          onNotify("Não foi possível identificar a anotação clínica.", "error");
          return;
        }

        await updateClinicalNote(noteId, payload);
        onNotify("Anotação clínica atualizada.", "success");
      } else {
        await createClinicalNote(prontuarioId, payload);
        onNotify("Anotação clínica registrada.", "success");
      }

      onSaved();
    } catch (err: unknown) {
      onNotify(
        getApiErrorMessage(err, "Não foi possível salvar a anotação clínica."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <FormGroup>
            <FormLabel>{labels.session_selection}</FormLabel>
            <Select
              value={formValues.session_selection}
              onChange={(event) =>
                handleChange("session_selection", String(event.target.value))
              }
              displayEmpty
              disabled={sessionsLoading}
            >
              <MenuItem value="">
                <em>
                  {sessionsLoading
                    ? "Carregando sessões..."
                    : "Sem vínculo com sessão ou atendimento"}
                </em>
              </MenuItem>
              {sessionOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {sessionsError && (
              <FormHelperText error>
                {sessionsError}{" "}
                <Button size="small" onClick={fetchSessions} sx={{ p: 0, minWidth: "auto", textTransform: "none" }}>
                  Tentar novamente
                </Button>
              </FormHelperText>
            )}
            {sessionOptions.length === 0 && !sessionsLoading && !sessionsError && (
              <FormHelperText>
                Nenhuma sessão agendada ou atendimento encontrado para este colaborador.
                A anotação pode ser salva sem vínculo.
              </FormHelperText>
            )}
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormGroup>
            <FormLabel>{labels.tipo_anotacao}</FormLabel>
            <Select
              value={formValues.tipo_anotacao}
              onChange={(event) =>
                handleChange("tipo_anotacao", event.target.value as ClinicalNoteType)
              }
            >
              {Object.entries(noteTypeLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <FormGroup>
            <FormLabel>{labels.titulo}</FormLabel>
            <TextField
              value={formValues.titulo}
              onChange={(event) => handleChange("titulo", event.target.value)}
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
            Evolução clínica
          </Typography>
        </Grid>

        {narrativeFields.map((field) => (
          <Grid item xs={12} md={6} key={field}>
            <FormGroup>
              <FormLabel>{labels[field]}</FormLabel>
              <TextField
                value={formValues[field]}
                onChange={(event) => handleChange(field, event.target.value)}
                multiline
                minRows={3}
              />
            </FormGroup>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
            SOAP
          </Typography>
        </Grid>

        {soapFields.map((field) => (
          <Grid item xs={12} md={6} key={field}>
            <FormGroup>
              <FormLabel>{labels[field]}</FormLabel>
              <TextField
                value={formValues[field]}
                onChange={(event) => handleChange(field, event.target.value)}
                multiline
                minRows={2}
              />
            </FormGroup>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700 }}>
            Escalas e tags
          </Typography>
        </Grid>

        {scoreFields.map((field) => (
          <Grid item xs={12} md={2.4} key={field}>
            <FormGroup>
              <FormLabel>{labels[field]}</FormLabel>
              <TextField
                type="number"
                value={formValues[field]}
                inputProps={{ min: 1, max: 5 }}
                onChange={(event) =>
                  handleChange(field, normalizeScore(Number(event.target.value)))
                }
              />
            </FormGroup>
          </Grid>
        ))}

        <Grid item xs={12}>
          <FormGroup>
            <FormLabel>{labels.tags}</FormLabel>
            <TextField
              value={formValues.tags}
              onChange={(event) => handleChange("tags", event.target.value)}
              placeholder="ex: ansiedade, depressão, crise"
              helperText="Separe múltiplas tags por vírgula."
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button type="button" variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? undefined : <SaveIcon />}
              disabled={loading}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size="1.25rem" />
                  Salvando anotação...
                </Box>
              ) : (
                "Salvar anotação"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClinicalNoteForm;
